import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState, type FormEvent, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Plus } from "lucide-react";
import { AdminRole, DEFAULT_PAGE_SIZE } from "@cpg/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { DataTable, type DataTableColumn } from "@/components/data-table";
import { createMerchant, listMerchants } from "@/api/merchants";
import { ApiError } from "@/api/client";
import { type MerchantResponse } from "@/api/types";
import { hasRole } from "@/lib/roles";
import { formatStatusLabel } from "@/lib/utils";
import { StatusBadge } from "@/components/status-badge";
import { MERCHANT_STATUS_COLOR } from "@/lib/status-colors";

// Real implementation of Task 25 Part C: the merchants list, upgraded to the
// shared DataTable. `GET /v1/merchants` has no pagination/sort/filter
// support on the backend (returns the full array, always), so this page
// fetches the full list once and drives DataTable client-side — a plain
// local `useState<{ page, sortBy, sortOrder, search }>`, not
// `lib/table-state.ts`'s `useTableState` (that hook assumes server-driven
// pagination via URL search params; a client-side derived list from an
// already-in-memory array doesn't need URL persistence). This is a
// deliberate, disclosed deviation from the "one shared table kit,
// URL-persisted" convention every other Phase-4 list page uses, forced by
// this endpoint's actual (unpaginated) shape — see task-25-report.md.
//
// Row detail is now a real route (`/merchants/$merchantId`, Part D) rather
// than the old in-page `MerchantDetailDialog` — that dialog (and its
// access-toggle logic) is removed entirely; the toggle now lives in the
// detail page's Overview tab. `CreateMerchantDialog` is unchanged.
export const Route = createFileRoute("/_app/merchants")({
  component: MerchantsPage,
});

function formatTimestamp(value: string): string {
  return new Date(value).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
}

// ─── create merchant dialog ─────────────────────────────────────────────────
//
// Unchanged from the pre-Task-25 page — controlled form (plain useState,
// matching login.tsx's style), only rendered/mounted for
// MERCHANTS_MANAGER/SUPERADMIN by the parent.

function CreateMerchantDialog() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [businessName, setBusinessName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setBusinessName("");
    setEmail("");
    setPassword("");
    setError(null);
  };

  const createMutation = useMutation({
    mutationFn: () => createMerchant({ businessName, email, password }),
    onSuccess: () => {
      toast.success("Merchant created.");
      queryClient.invalidateQueries({ queryKey: ["merchants"] });
      setOpen(false);
      reset();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "Failed to create merchant.");
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!businessName.trim() || !email.trim() || !password) {
      setError("All fields are required.");
      return;
    }
    createMutation.mutate();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4" />
          Create merchant
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create merchant</DialogTitle>
          <DialogDescription>Creates a new merchant account with immediate access.</DialogDescription>
        </DialogHeader>

        {error && (
          <div className="rounded-md border border-[#EF4444]/30 bg-[#EF4444]/5 px-3 py-2 text-sm text-[#EF4444]">
            {error}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="businessName">Business name</Label>
            <Input
              id="businessName"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              autoComplete="organization"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Email address</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
            <p className="text-xs text-[#6B7280]">
              At least 8 characters, with one digit and one special character.
            </p>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={createMutation.isPending} className="w-full sm:w-auto">
              {createMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Create merchant"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── row → detail navigation ─────────────────────────────────────────────────
//
// DataTable is purely presentational (no onRowClick concept) and is out of
// scope to modify for this task, so every cell's content is wrapped in a
// <Link> that fills the cell (negating TableCell's own padding with `-m-2
// p-2`) — clicking anywhere in a row is a real navigation to the detail
// route, the same effect as a `tr onClick` without touching shared-kit code.

function RowLink({ merchantId, children }: { merchantId: string; children: ReactNode }) {
  return (
    <Link to="/merchants/$merchantId" params={{ merchantId }} className="group -m-2 block p-2">
      {children}
    </Link>
  );
}

// ─── merchants page ──────────────────────────────────────────────────────────

interface ListState {
  page: number;
  pageSize: number;
  sortBy: string | null;
  sortOrder: "asc" | "desc";
  search: string;
}

function MerchantsPage() {
  const { admin } = Route.useRouteContext();
  const canManage = hasRole(admin.roles, AdminRole.MERCHANTS_MANAGER);

  const { data, isPending, isError } = useQuery({
    queryKey: ["merchants"],
    queryFn: listMerchants,
  });

  const [state, setState] = useState<ListState>({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    sortBy: null,
    sortOrder: "asc",
    search: "",
  });

  const filteredSorted = useMemo(() => {
    const list = data ?? [];
    const term = state.search.trim().toLowerCase();
    const filtered = term
      ? list.filter(
          (m) => m.businessName.toLowerCase().includes(term) || m.email.toLowerCase().includes(term),
        )
      : list;

    if (!state.sortBy) return filtered;

    const direction = state.sortOrder === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      switch (state.sortBy) {
        case "businessName":
          return a.businessName.localeCompare(b.businessName) * direction;
        case "email":
          return a.email.localeCompare(b.email) * direction;
        case "createdAt":
          return a.createdAt.localeCompare(b.createdAt) * direction;
        default:
          return 0;
      }
    });
  }, [data, state.search, state.sortBy, state.sortOrder]);

  const total = filteredSorted.length;
  const pageItems = filteredSorted.slice(
    (state.page - 1) * state.pageSize,
    state.page * state.pageSize,
  );

  const columns: DataTableColumn<MerchantResponse>[] = [
    {
      key: "businessName",
      header: "Business name",
      sortable: true,
      className: "pl-4",
      cell: (m) => (
        <RowLink merchantId={m.id}>
          <span className="group-hover:underline">{m.businessName}</span>
        </RowLink>
      ),
    },
    {
      key: "email",
      header: "Email",
      sortable: true,
      cell: (m) => (
        <RowLink merchantId={m.id}>
          <span className="text-muted-foreground">{m.email}</span>
        </RowLink>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (m) => (
        <RowLink merchantId={m.id}>
          <StatusBadge label={formatStatusLabel(m.status)} color={MERCHANT_STATUS_COLOR[m.status]} />
        </RowLink>
      ),
    },
    {
      key: "access",
      header: "Access",
      cell: (m) => (
        <RowLink merchantId={m.id}>
          <StatusBadge
            label={m.accessGranted ? "Access granted" : "Access revoked"}
            color={m.accessGranted ? "success" : "danger"}
          />
        </RowLink>
      ),
    },
    {
      key: "createdAt",
      header: "Created",
      sortable: true,
      cell: (m) => (
        <RowLink merchantId={m.id}>
          <span className="text-muted-foreground">{formatTimestamp(m.createdAt)}</span>
        </RowLink>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <Input
          value={state.search}
          onChange={(e) => setState((s) => ({ ...s, search: e.target.value, page: 1 }))}
          placeholder="Filter by business name or email…"
          className="w-64"
        />
        {canManage && <CreateMerchantDialog />}
      </div>

      <DataTable
        columns={columns}
        items={pageItems}
        total={total}
        page={state.page}
        pageSize={state.pageSize}
        sortBy={state.sortBy}
        sortOrder={state.sortOrder}
        loading={isPending}
        error={isError ? "Failed to load merchants." : null}
        onPageChange={(page) => setState((s) => ({ ...s, page }))}
        onPageSizeChange={(pageSize) => setState((s) => ({ ...s, pageSize, page: 1 }))}
        onSortChange={(columnKey) =>
          setState((s) =>
            s.sortBy === columnKey
              ? { ...s, sortOrder: s.sortOrder === "asc" ? "desc" : "asc" }
              : { ...s, sortBy: columnKey, sortOrder: "asc" },
          )
        }
        getRowKey={(m) => m.id}
        emptyMessage="No merchants match your filter."
      />
    </div>
  );
}
